import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LoadingMessage } from '@/components/common/Spinner';
import { CreateAccountModal } from '@/components/modals/CreateAccountModal';
import { Titlebar } from '@/components/layout/Page';
import { BudgetPage } from '@/components/budget/BudgetPage';
import { AccountPage } from '@/components/accounts/AccountPage';
import { ReportsPage } from '@/components/reports/ReportsPage';
import { SchedulesPage } from '@/components/schedules/SchedulesPage';
import { SettingsPage } from '@/components/settings/SettingsPage';
import { FeatureUnavailable } from '@/components/FeatureUnavailable';
import { Sidebar } from '@/components/sidebar/Sidebar';
import { useAppStore } from '@/store/app';
import { usePrefs } from '@/store/prefs';

export function App() {
  const prefsReady = usePrefs(state => state.ready);
  const appReady = useAppStore(state => state.ready);
  const [bootError, setBootError] = useState<string | null>(null);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [floating, setFloating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        await Promise.all([
          usePrefs.getState().load(),
          useAppStore.getState().load(),
        ]);
      } catch (error) {
        if (!cancelled) {
          setBootError(error instanceof Error ? error.message : String(error));
        }
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, []);

  if (bootError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-page-background p-6 text-center">
        <p className="text-md font-medium text-error-text">
          模拟接口启动失败。
        </p>
        <p className="max-w-md font-mono text-xs text-error-text">
          {bootError}
        </p>
        <p className="text-sm text-page-text-light">
          请运行 <code>npm run dev</code>，并确认{' '}
          <code>public/mockServiceWorker.js</code> 存在。
        </p>
      </div>
    );
  }

  if (!prefsReady || !appReady) {
    return (
      <div className="flex h-full items-center justify-center bg-page-background">
        <LoadingMessage message="正在加载预算…" />
      </div>
    );
  }

  return (
    <div className="relative flex h-full overflow-hidden bg-page-background">
      <Sidebar
        floating={floating}
        onToggleFloating={() => setFloating(value => !value)}
        onAddAccount={() => setAddAccountOpen(true)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Titlebar />
        <Routes>
          <Route path="/" element={<Navigate to="/budget" replace />} />
          <Route path="/budget" element={<BudgetPage />} />
          <Route path="/reports/*" element={<ReportsPage />} />
          <Route path="/schedules" element={<SchedulesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/accounts" element={<AccountPage accountId="all" />} />
          <Route
            path="/accounts/onbudget"
            element={<AccountPage accountId="onbudget" />}
          />
          <Route
            path="/accounts/offbudget"
            element={<AccountPage accountId="offbudget" />}
          />
          <Route
            path="/accounts/:accountId"
            element={<AccountPage />}
          />
          {/* Present in upstream's nav but outside this clone's scope. */}
          <Route path="/payees" element={<FeatureUnavailable name="收款人" />} />
          <Route path="/rules" element={<FeatureUnavailable name="规则" />} />
          <Route path="/tags" element={<FeatureUnavailable name="标签" />} />
          <Route
            path="/bank-sync"
            element={<FeatureUnavailable name="银行同步" />}
          />
          <Route path="/*" element={<Navigate to="/budget" replace />} />
        </Routes>
      </div>
      {addAccountOpen && (
        <CreateAccountModal onClose={() => setAddAccountOpen(false)} />
      )}
    </div>
  );
}
